import React, { useCallback, useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { Button, Card, Modal } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { NotificationContainer, NotificationManager } from "react-notifications";
import * as Yup from "yup";
import { Breadcrumb } from "@gull";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { caricaComuniTypeahed } from "app/views/tabellaPocr/Comuni/comuniService";
import { caricaAslDaComune } from "app/views/tabellaPocr/Asl/aslService";
import jwtAuthService from "app/services/jwtAuthService";
import { authorRoleOptions, getQuery, getQueryPostDto, queryServer } from "app/views/tabellaPocr/ServiceController";
import { handleTabSelect } from "app/utility/typeahead";
import { formattaDataOraHHmmss } from "app/utility/dataOra";
import LoadingSpinner from "@gull/components/Loading/LoadingSpinner";
import SupremoCard from "app/views/pages/supremoCard";
import EditorHtmlModal from "app/views/tabellaPocr/Editor/EditorHtmlModal";
import OpzioniAccessModal from "app/views/tabellaPocr/Opzioni/OpzioniAccessModal";

const PIEMONTE_COD_REGIONE = "010";
const VENETO_COD_REGIONE = "050";

const normalizeCode = (value) => String(value ?? "").trim();

const normalizeCodRegione = (value) => normalizeCode(value).padStart(3, "0");

const isCodRegione = (codRegione, expectedCodRegione) =>
    normalizeCodRegione(codRegione) === expectedCodRegione;

const isPiemonteCodRegione = (codRegione) => isCodRegione(codRegione, PIEMONTE_COD_REGIONE);

const isVenetoCodRegione = (codRegione) => isCodRegione(codRegione, VENETO_COD_REGIONE);

const hasAslWithCodRegione = (aslList, codRegione) =>
    (Array.isArray(aslList) ? aslList : []).some((asl) =>
        isCodRegione(asl?.codRegione, codRegione)
    );

const hasPiemonteAsl = (aslList) => hasAslWithCodRegione(aslList, PIEMONTE_COD_REGIONE);

const hasVenetoAsl = (aslList) => hasAslWithCodRegione(aslList, VENETO_COD_REGIONE);

const getAslCodRegione = (asl) => asl?.codRegione ?? "";

const getAslCodAzienda = (asl) => asl?.codAzienda ?? "";

const emptyDatiAggiuntivi = () => ({
    iddati: 0,
    idregistro: 0,
    nomeUtente: "",
    password: "",
    codiceFiscale: "",
});

const normalizeDatiAggiuntivi = (datiAggiuntivi) => {
    const normalized = {
        ...emptyDatiAggiuntivi(),
        ...(datiAggiuntivi || {}),
    };

    return {
        ...normalized,
        nomeUtente: normalized.nomeUtente ?? "",
        password: normalized.password ?? "",
        codiceFiscale: normalized.codiceFiscale ?? "",
    };
};

const clearPiemonteFields = (setFieldValue) => {
    setFieldValue("codiceAzienda", "");
    setFieldValue("codiceLuogoAccettazione", "");
};

const clearVenetoFields = (setFieldValue) => {
    setFieldValue("datiAggiuntivi", emptyDatiAggiuntivi());
};

const InfoAccount = () => {
    const location = useLocation();
    const hide = location.state?.hide;
    const edit = location.state?.edit ?? false;

    const data = jwtAuthService.getUser();
    const displayName = data?.user?.displayName;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const effectRan = useRef(false);

    const [comuniOptions, setComuniOptions] = useState([]);
    const [isMultiCap, setIsMultiCap] = useState(false);
    const [elencoCap, setElencoCap] = useState([]);
    const [ambulatoriOptions, setAmbulatoriOptions] = useState([]);
    const [aslOptions, setAslOptions] = useState([]);
    const [esami, setEsami] = useState();
    const [numEsami, setNumEsami] = useState(0);
    const [isEsamiButtonDisabled, setEsamiButtonDisabled] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Stato per modali
    const [isRefertoModalOpen, setRefertoModalOpen] = useState(false);
    const [isFirmaModalOpen, setFirmaModalOpen] = useState(false);
    const [isEsamiModalOpen, setEsamiModalOpen] = useState(false);
    const [variabiliHtml, setVariabiliHtml] = useState([]);
    const [isVariabiliHtmlLoading, setVariabiliHtmlLoading] = useState(false);
    const [variabiliHtmlError, setVariabiliHtmlError] = useState("");
    const variabiliHtmlCaricate = useRef(false);
    const [showOpzioniTrigger, setShowOpzioniTrigger] = useState(false);
    const [showOpzioniModal, setShowOpzioniModal] = useState(false);

    const canUseOpzioniShortcut = ["SUPERADMIN", "ADMIN", "FSE"].some((role) =>
        data?.user?.ruoli?.includes(role)
    );

    const routeSegments = hide
        ? [{ name: "Informazioni Account" }]
        : [
            { name: "Home", path: "/" },
            { name: "Informazioni Account" },
        ];

    const [contentReferto, setContentReferto] = useState('');
    const [contentFirma, setContentFirma] = useState('');

    useEffect(() => {
        if (!canUseOpzioniShortcut) return undefined;

        const handleOpzioniShortcut = (event) => {
            if (event.ctrlKey && event.altKey && event.code === "F8" && !event.repeat) {
                event.preventDefault();
                setShowOpzioniTrigger((currentValue) => !currentValue);
            }
        };

        window.addEventListener("keydown", handleOpzioniShortcut);
        return () => window.removeEventListener("keydown", handleOpzioniShortcut);
    }, [canUseOpzioniShortcut]);

    useEffect(() => {
        if (!isRefertoModalOpen || variabiliHtmlCaricate.current) return;

        setVariabiliHtmlLoading(true);
        setVariabiliHtmlError("");

        dispatch(queryServer("VariabiliHtml/GetVariables"))
            .then((result) => {
                setVariabiliHtml(Array.isArray(result) ? result : []);
                variabiliHtmlCaricate.current = true;
            })
            .catch((error) => {
                console.error("Errore durante il caricamento delle variabili HTML:", error);
                setVariabiliHtmlError(
                    "Impossibile caricare le variabili. Verificare che il ruolo FSE sia autorizzato."
                );
            })
            .finally(() => setVariabiliHtmlLoading(false));
    }, [dispatch, isRefertoModalOpen]);
    const [initialValues, setInitialValues] = useState({
        // SEZIONE 1
        intestazione: displayName,
        idComune: 0,
        comuneAsString: "",
        cap: "",
        indirizzo: "",
        telefono: "",
        partitaIva: "",
        codiceFiscale: "",
        codiceFiscaleMedico: "",

        // SEZIONE 2
        codiceRegione: "",
        codiceAsl: "",
        codiceUfficio: "",
        codiceAzienda: "",
        codiceLuogoAccettazione: "",
        datiAggiuntivi: emptyDatiAggiuntivi(),
        pinSmartCard: "",
        authRoleFse: "",
        tipoCodice: 0,

        // SEZIONE 3
        idAmbulatorio: 0,
        ambulatorioAsString: "",

        // SEZIONE 4
        intestazioneReferto: "<p>Inserire intestazione referto...</p>",
        firmaMedico: "<p>Inserire firma medico...</p>",
        allineamentoFirma: "2",
        dimensioniTitolo: "16",
        addSezioniFse: false,
        addFirma: true,

        // UTILI PER MODIFICA
        idasl: 0,
        idstruttura: 0,
        idutente: "",
        idintestazione: 0,
        idsede: 0,
        idregistro: 0,
        isEdit: false,
        isLater: true,
    });

    const handleMultiCap = useCallback((cap) => {
        if (!cap?.endsWith("xx")) {
            setElencoCap([]);
            setIsMultiCap(false);
            return;
        }

        dispatch(getQueryPostDto("Comuni/ElencoMultiCap", cap))
            .then((result) => {
                if (result.length > 1) {
                    setElencoCap(result);
                    setIsMultiCap(true);
                } else {
                    setElencoCap([]);
                    setIsMultiCap(false);
                }
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
                setElencoCap([]);
                setIsMultiCap(false);
            });
    }, [dispatch]);

    const loadAslDaComune = useCallback((idComune) => {
        if (!idComune) {
            setAslOptions([]);
            return Promise.resolve([]);
        }

        return dispatch(caricaAslDaComune(idComune))
            .then((data) => {
                const sortedData = (Array.isArray(data) ? data : []).sort((a, b) =>
                    (a.regione || "").localeCompare(b.regione || "") ||
                    (a.descrizione || "").localeCompare(b.descrizione || "")
                );
                setAslOptions(sortedData);
                return sortedData;
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
                setAslOptions([]);
                return [];
            });
    }, [dispatch]);

    useEffect(() => {
        // Previene la seconda chiamata in StrictMode
        if (effectRan.current) return;
        effectRan.current = true;

        // Carico gli ambulatori
        dispatch(getQuery("ProfiloFse/GetAmbulatori"))
            .then((data) => {
                setAmbulatoriOptions(data);
            })
            .catch((err) => console.log(err));

        // Carico le Asl
        // Carico le informazioni se sono in edit
        if (edit) {
            dispatch(getQuery("ProfiloFse/GetProfilo"))
                .then(async (res) => {
                    if (res.isSuccess) {
                        // Destruttura "esami" dal resto delle proprietà
                        const { esami, ...otherValues } = res.data;
                        otherValues.datiAggiuntivi = normalizeDatiAggiuntivi(
                            otherValues.datiAggiuntivi || otherValues.DatiAggiuntivi
                        );

                        let isComunePiemonte = false;
                        let isComuneVeneto = false;
                        if (otherValues.idComune) {
                            const aslDaComune = await loadAslDaComune(otherValues.idComune);
                            isComunePiemonte = hasPiemonteAsl(aslDaComune);
                            isComuneVeneto = hasVenetoAsl(aslDaComune);
                            const selectedAsl = aslDaComune.find(
                                (asl) => asl.id?.toString() === otherValues.idasl?.toString()
                            );

                            if (selectedAsl) {
                                otherValues.codiceRegione = otherValues.codiceRegione || getAslCodRegione(selectedAsl) || "";
                                otherValues.codiceAsl = otherValues.codiceAsl || getAslCodAzienda(selectedAsl) || "";
                            }
                        }
                        if (!isPiemonteCodRegione(otherValues.codiceRegione) && !isComunePiemonte) {
                            otherValues.codiceAzienda = "";
                            otherValues.codiceLuogoAccettazione = "";
                        }
                        if (!isVenetoCodRegione(otherValues.codiceRegione) && !isComuneVeneto) {
                            otherValues.datiAggiuntivi = emptyDatiAggiuntivi();
                        }
                        // Salvi i soli otherValues dentro initialValues
                        setInitialValues(otherValues);
                        if (otherValues.cap?.length === 5) {
                            handleMultiCap(`${otherValues.cap.slice(0, -2)}xx`);
                        }
                        // Intetestazione referto 
                        setContentReferto(otherValues.intestazioneReferto);
                        setContentFirma(otherValues.firmaMedico);
                        // E gestisci esami e relative variabili di stato
                        setEsami(esami);
                        setNumEsami(esami.length);
                        setEsamiButtonDisabled(esami.length === 0);
                    }
                    else {
                        NotificationManager.warning(res.message, "Attenzione",);
                    }
                })
                .catch((err) => console.log(err));
        }
    }, [dispatch, edit, handleMultiCap, loadAslDaComune]);

    const handleComuneSearch = (query, setFieldValue) => {
        if (query.length >= 2) {
            dispatch(caricaComuniTypeahed(query))
                .then((data) => {
                    setComuniOptions(data);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const handleComuneChange = (selected, setFieldValue) => {
        if (selected && selected.length > 0) {
            const [comune] = selected;
            const cap = comune.extra || "";

            setFieldValue("idComune", String(comune.id));
            setFieldValue("comuneAsString", comune.descrizione);
            setFieldValue("idasl", "");
            setFieldValue("codiceRegione", "");
            setFieldValue("codiceAsl", "");
            clearPiemonteFields(setFieldValue);
            clearVenetoFields(setFieldValue);

            if (cap.endsWith("xx")) {
                setFieldValue("cap", "");
                handleMultiCap(cap);
            } else {
                setFieldValue("cap", cap);
                setElencoCap([]);
                setIsMultiCap(false);
            }

            loadAslDaComune(comune.id).then((aslDaComune) => {
                if (!hasPiemonteAsl(aslDaComune)) {
                    clearPiemonteFields(setFieldValue);
                }
                if (!hasVenetoAsl(aslDaComune)) {
                    clearVenetoFields(setFieldValue);
                }
            });
        } else {
            setFieldValue("idComune", "");
            setFieldValue("comuneAsString", "");
            setFieldValue("cap", "");
            setFieldValue("idasl", "");
            setFieldValue("codiceRegione", "");
            setFieldValue("codiceAsl", "");
            clearPiemonteFields(setFieldValue);
            clearVenetoFields(setFieldValue);
            setElencoCap([]);
            setIsMultiCap(false);
            setAslOptions([]);
        }
    };

    const handleAmbulatorioChange = async (e, formikOnChange) => {
        formikOnChange(e);
        const idAmbulatorio = e.target.value;

        if (!idAmbulatorio) {
            setNumEsami(0);
            setEsami([]);
            setEsamiButtonDisabled(true);
            return;
        }

        try {
            const dataEsami = await dispatch(getQuery(`ProfiloFse/GetEsamiByAmbulatorio/${idAmbulatorio}`));
            setEsami(dataEsami);
            setNumEsami(dataEsami.length);
            setEsamiButtonDisabled(false);
        } catch (error) {
            console.log(error);
        }
    };

    const handleIntestazioneReferto = (e, formikOnBlur, values, setFieldValue) => {
        formikOnBlur(e);

        if (values.isLater || !edit) {
            const nuovaIntestazioneReferto = `
            <p><strong>${values.intestazione || ""}</strong>
            <br/>${values.indirizzo || ""} - ${values.comuneAsString.replace(/\s*\[.*?\]/g, "") || ""} - ${values.cap || ""}
            <br/>P.Iva: ${values.partitaIva || ""} | Tel: ${values.telefono || ""}
            </p>
            `;
            setFieldValue("intestazioneReferto", nuovaIntestazioneReferto, false);
            setContentReferto(nuovaIntestazioneReferto);
        }
    }

    const handleFirmaReferto = (e, formikOnBlur, values, setFieldValue) => {
        formikOnBlur(e);

        if (values.isLater || !edit) {
            const nuovaFirma = `
            <div><strong>${values.intestazione || ""}</strong></div>
            `;
            setFieldValue("firmaMedico", nuovaFirma, false);
            setContentFirma(nuovaFirma);
        }
    }

    const handleLater = () => {
        if (edit)
            navigate("/");
        else
            handleSubmit(initialValues, { setSubmitting: null });
    }

    const handleSubmit = (values, { setSubmitting }) => {
        const isPiemonte = isPiemonteCodRegione(values.codiceRegione) || hasPiemonteAsl(aslOptions);
        const isVeneto = isVenetoCodRegione(values.codiceRegione) || hasVenetoAsl(aslOptions);
        const submitValues = {
            ...values,
            datiAggiuntivi: normalizeDatiAggiuntivi(values.datiAggiuntivi),
        };

        if (!isPiemonte) {
            submitValues.codiceAzienda = "";
            submitValues.codiceLuogoAccettazione = "";
        }

        if (!isVeneto) {
            submitValues.datiAggiuntivi = emptyDatiAggiuntivi();
        } else if (!submitValues.codiceRegione) {
            submitValues.codiceRegione = VENETO_COD_REGIONE;
        }

        setIsSaving(true);
        NotificationManager.listNotify.forEach(n => NotificationManager.remove({ id: n.id }));
        setSubmitting && setSubmitting(true);
        dispatch(getQueryPostDto("ProfiloFse/EditProfilo", submitValues))
            .then((result) => {
                if (result.isSuccess) {
                    NotificationManager.success(result.message, "Salva");
                    setTimeout(() => {
                        navigate("/");
                    }, 1000);
                }
                else {
                    NotificationManager.error(result.message, "Errore");
                }
                setSubmitting && setSubmitting(false);
                setTimeout(() => {
                    setIsSaving(false);
                }, 800);
            })
            .catch((error) => {
                console.error(error);
                setSubmitting && setSubmitting(false);
                setIsSaving(false);
            });
    };

    const [consensi, setConsensi] = useState([]);
    const [isLoadingConsensi, setLoadingConsensi] = useState(true);

    const [saldiWa, setSaldiWa] = useState([]);
    useEffect(() => {
        dispatch(getQuery("NotificaWallet/GetSaldiCorrente"))
            .then((data) => setSaldiWa(data ?? []))
            .catch(() => setSaldiWa([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const saldoTipoWa = (tipo) => {
        const list = Array.isArray(saldiWa) ? saldiWa : [];
        const w = list.find((x) => Number(x.tipo) === Number(tipo));
        return w ? w.saldo : 0;
    };

    useEffect(() => {
        loadConsensi();
    }, []);

    const loadConsensi = () => {
        dispatch(getQuery("RegistroTrattamenti/GetTrattamenti"))
            .then((res) => {
                setConsensi(res);
                setLoadingConsensi(false);
            })
            .catch((err) => {
                console.error("Errore nel caricamento consensi", err);
                setLoadingConsensi(false);
            });
    }

    const handleAggiornaConsensi = () => {
        const consensiModificabili = consensi.filter(c => c.tipologia !== 1);
        dispatch(getQueryPostDto("RegistroTrattamenti/SalvaMulti", consensiModificabili))
            .then((res) => {
                if (res.isSuccess) {
                    NotificationManager.success("Consensi aggiornati con successo", "OK");
                    loadConsensi();
                } else {
                    NotificationManager.error(res.message || "Errore nel salvataggio", "Errore");
                }
            })
            .catch((err) => {
                console.error("Errore durante il salvataggio dei consensi", err);
                NotificationManager.error("Errore imprevisto", "Errore");
            });
    };

    return (
        <div>
            <Breadcrumb routeSegments={routeSegments} />

            {showOpzioniTrigger && canUseOpzioniShortcut && (
                <div className="d-flex justify-content-end mb-3">
                    <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowOpzioniModal(true)}
                    >
                        <i className="i-Gear"></i>&nbsp;&nbsp;Opzioni avanzate
                    </Button>
                </div>
            )}

            <OpzioniAccessModal
                show={showOpzioniModal}
                onClose={() => setShowOpzioniModal(false)}
            />

            <section className="chekout-page">
                {initialValues.isLater &&
                    <div className="alert alert-success d-flex justify-content-between align-items-center">
                        <span className="flex-1">
                            👀 Vuoi guardarti un attimo intorno, poi penserai a registrarti?
                        </span>

                        <div className="flex gap-2">
                            <button
                                onClick={handleLater}
                                className="btn btn-primary"
                            >
                                <i className="i-Home-4"></i>&nbsp;&nbsp;Vai alla Dashboard
                            </button>
                        </div>
                    </div>
                }
                <div className="row">
                    <div className="col-3">
                        <div className="card">
                            <div className="card-body">
                                <div className="card-title">Perché devo inserire queste informazioni?</div>
                                <div className="ul-widget__body">
                                    <div className="ul-widget3">
                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">
                                                            Informazioni Studio
                                                        </span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione uno
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    1)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    I dati principali del tuo studio medico consentono l'inizializzazione corretta di tutte le procedure. Queste informazioni personalizzano il programma secondo le tue esigenze. Assicurati che i dati siano sempre aggiornati.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">Credenziali Fascicolo Sanitario Elettronico</span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione due
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    2)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    Inserisci le credenziali per accedere al Fascicolo Sanitario Elettronico in modo sicuro e conforme alle normative. Questo passaggio garantisce l’integrazione dei dati clinici e la condivisione in tempo reale. Proteggi sempre le credenziali come previsto dalle regole sulla privacy.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">Selezione Esami</span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione tre
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    3)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    Scegli l’ambulatorio appropriato in base alle esigenze cliniche del tuo studio. In questo modo, verranno associati automaticamente tutti gli esami pertinenti secondo il Nomenclatore Nazionale. Puoi verificare e confermare gli esami tramite l’apposito pulsante alla destra della scelta dell’ambulatorio.</p>
                                            </div>
                                        </div>
                                        <div className="ul-widget3-item">
                                            <div className="ul-widget3-header">
                                                <div className="ul-widget3-info">
                                                    <Link className="__g-widget-username">
                                                        <span className="t-font-bolder">Configurazione Referto</span>
                                                    </Link>
                                                    <br />
                                                    <span className="ul-widget-notification-item-time">
                                                        #Sezione quattro
                                                    </span>
                                                </div>
                                                <span className="ul-widget3-status text-success t-font-bolder">
                                                    4)
                                                </span>
                                            </div>
                                            <div className="ul-widget3-body">
                                                <p className="ul-widget3-text">
                                                    Definisci le modalità di presentazione e stampa del referto per garantirne la massima chiarezza. Personalizza gli elementi informativi indispensabili come intestazione e firma. Così facendo, il referto rispetterà gli standard professionali del tuo studio.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card mt-3">
                            <div className="card-body">
                                <h6 className="card-title">Consensi al Trattamento</h6>
                                {isLoadingConsensi ? (
                                    <p>Caricamento consensi...</p>
                                ) : (
                                    <>
                                        {consensi.map((consenso) => (
                                            <div key={consenso.id} className="mb-4 border-bottom pb-3">
                                                <div className="mb-3">
                                                    <fieldset>
                                                        <h5 className="fw-bold">{consenso.tipologiaString}</h5>
                                                        <div className="mb-2">
                                                            <strong>Data consenso:</strong> {formattaDataOraHHmmss(consenso.dataConsenso)}
                                                        </div>
                                                        {consenso.tipologia === 1 ? (
                                                            <>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        disabled
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === true}
                                                                        readOnly
                                                                    /> Acconsento
                                                                </label>
                                                                <span className="mx-4"></span>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        disabled
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === false}
                                                                        readOnly
                                                                    /> Non acconsento
                                                                </label>
                                                                <div className="text-muted fst-italic">(Non modificabile)</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        id={`acconsento_${consenso.id}`}
                                                                        name={`consenso_${consenso.id}`}
                                                                        value="accept"
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === true}
                                                                        onChange={() => {
                                                                            setConsensi(prev =>
                                                                                prev.map(c => c.id === consenso.id ? { ...c, acconsento: true } : c)
                                                                            );
                                                                        }}
                                                                    /> Acconsento
                                                                </label>
                                                                <span className="mx-4"></span>
                                                                <label>
                                                                    <input
                                                                        type="radio"
                                                                        id={`nonacconsento_${consenso.id}`}
                                                                        name={`consenso_${consenso.id}`}
                                                                        value="decline"
                                                                        className="form-check-input"
                                                                        checked={consenso.acconsento === false}
                                                                        onChange={() => {
                                                                            setConsensi(prev =>
                                                                                prev.map(c => c.id === consenso.id ? { ...c, acconsento: false } : c)
                                                                            );
                                                                        }}
                                                                    /> Non acconsento
                                                                </label>
                                                            </>
                                                        )}
                                                    </fieldset>
                                                </div>
                                            </div>
                                        ))}

                                        <Button className="w-100 mt-2" variant="primary" onClick={handleAggiornaConsensi}>
                                            <i className="i-Check"></i>&nbsp;&nbsp;Aggiorna Consensi
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                        {!initialValues.isLater &&
                            <div className="mt-3">
                                <SupremoCard btnWidth="100" textAlign="text-left" />
                            </div>
                        }
                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="card-title">
                                    <i className="i-Speach-Bubble-3 me-1"></i> Crediti Notifiche WhatsApp
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>Referto (Utility)</span>
                                    <span className="badge bg-primary text-16">{saldoTipoWa(1)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Marketing</span>
                                    <span className="badge bg-info text-16">{saldoTipoWa(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-9">
                        <div className="container">
                            <Card className="mb-3">
                                <Card.Body>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={handleSubmit}
                                        enableReinitialize={true}
                                        validateOnBlur={true}
                                        validateOnChange={false}
                                    >
                                        {({
                                            values,
                                            handleChange,
                                            handleBlur,
                                            setFieldValue,
                                            errors,
                                            touched,
                                            isSubmitting
                                        }) => {
                                            const showPiemonteFields =
                                                isPiemonteCodRegione(values.codiceRegione) || hasPiemonteAsl(aslOptions);
                                            const showVenetoFields =
                                                isVenetoCodRegione(values.codiceRegione) || hasVenetoAsl(aslOptions);

                                            return (
                                                <>
                                                    {
                                                        isSaving && (
                                                            <div className="d-flex justify-content-center my-3">
                                                                <LoadingSpinner message="Salvataggio in corso..." />
                                                            </div>
                                                        )
                                                    }
                                                    {
                                                        !isSaving &&
                                                        <Form autoComplete="off">
                                                            <h5 className="fw-semibold">1) Informazioni Studio</h5>
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Intestazione</label>
                                                                    <input
                                                                        type="text"
                                                                        name="intestazione"
                                                                        id="intestazione"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                            handleFirmaReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.intestazione}
                                                                    />
                                                                    {errors.intestazione && touched.intestazione && (
                                                                        <div className="text-danger">{errors.intestazione}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label">Comune</label>
                                                                    <AsyncTypeahead
                                                                        id="idComune"
                                                                        name="idComune"
                                                                        labelKey="descrizione"
                                                                        selectHintOnEnter={true}
                                                                        onSearch={(query) => handleComuneSearch(query, setFieldValue)}
                                                                        options={comuniOptions}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Tab") {
                                                                                const selected = handleTabSelect(e, comuniOptions, setFieldValue, "idComune", "comuneAsString");
                                                                                handleComuneChange(selected, setFieldValue);
                                                                            }
                                                                        }}
                                                                        placeholder="Ricerca comune..."
                                                                        onBlur={(e) => handleIntestazioneReferto(e, handleBlur, values, setFieldValue)}
                                                                        onChange={(selected) => handleComuneChange(selected, setFieldValue)}
                                                                        selected={
                                                                            values.idComune
                                                                                ? [{ id: parseInt(values.idComune, 10), descrizione: values.comuneAsString }]
                                                                                : []
                                                                        }
                                                                    />
                                                                    {errors.idComune && touched.idComune && (
                                                                        <div className="text-danger">{errors.idComune}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-2 mb-3">
                                                                    <label className="form-label">CAP</label>
                                                                    {isMultiCap ? (
                                                                        <select
                                                                            name="cap"
                                                                            id="cap"
                                                                            autoComplete="off"
                                                                            className="form-control"
                                                                            value={values.cap}
                                                                            onChange={handleChange}
                                                                            onBlur={(e) => {
                                                                                handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                            }}
                                                                        >
                                                                            <option value="">Seleziona un CAP</option>
                                                                            {elencoCap.map((item, index) => (
                                                                                <option key={item.id ?? index} value={item.cap}>
                                                                                    {item.cap}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            name="cap"
                                                                            id="cap"
                                                                            autoComplete="off"
                                                                            className="form-control"
                                                                            onChange={(e) => {
                                                                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                                                                                if (e.target.value.length <= 5) {
                                                                                    handleChange(e);
                                                                                }
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                            }}
                                                                            value={values.cap}
                                                                        />
                                                                    )}
                                                                    {errors.cap && touched.cap && (
                                                                        <div className="text-danger">{errors.cap}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="row">
                                                                <div className="col-md-8 mb-3">
                                                                    <label className="form-label">Indirizzo</label>
                                                                    <input
                                                                        type="text"
                                                                        name="indirizzo"
                                                                        id="indirizzo"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.indirizzo}
                                                                    />
                                                                    {errors.indirizzo && touched.indirizzo && (
                                                                        <div className="text-danger">{errors.indirizzo}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label">Telefono</label>
                                                                    <input
                                                                        type="text"
                                                                        name="telefono"
                                                                        id="telefono"
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue);
                                                                        }}
                                                                        value={values.telefono}
                                                                    />
                                                                    {errors.telefono && touched.telefono && (
                                                                        <div className="text-danger">{errors.telefono}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Partita Iva</label>
                                                                    <input
                                                                        type="text"
                                                                        name="partitaIva"
                                                                        id="partitaIva"
                                                                        maxLength={11}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={(e) => {
                                                                            handleIntestazioneReferto(e, handleBlur, values, setFieldValue)
                                                                        }}
                                                                        value={values.partitaIva}
                                                                    />
                                                                    {errors.partitaIva && touched.partitaIva && (
                                                                        <div className="text-danger">{errors.partitaIva}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Codice Fiscale Struttura</label>
                                                                    <input
                                                                        type="text"
                                                                        name="codiceFiscale"
                                                                        id="codiceFiscale"
                                                                        maxLength={16}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={(e) => {
                                                                            e.target.value = e.target.value.toUpperCase();
                                                                            handleChange(e);
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        value={values.codiceFiscale}
                                                                    />
                                                                    {errors.codiceFiscale && touched.codiceFiscale && (
                                                                        <div className="text-danger">{errors.codiceFiscale}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-6 mb-3">
                                                                    <label className="form-label">Codice Fiscale Medico</label>
                                                                    <input
                                                                        type="text"
                                                                        name="codiceFiscaleMedico"
                                                                        id="codiceFiscaleMedico"
                                                                        maxLength={16}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={(e) => {
                                                                            e.target.value = e.target.value.toUpperCase();
                                                                            handleChange(e);
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        value={values.codiceFiscaleMedico}
                                                                    />
                                                                    {errors.codiceFiscaleMedico && touched.codiceFiscaleMedico && (
                                                                        <div className="text-danger">{errors.codiceFiscaleMedico}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <hr className="bg-primary" />
                                                            <h5 className="fw-semibold">2) Credenziali Fascicolo Sanitario Elettronico</h5>
                                                            <div className="row">
                                                                <div className="col-9 mb-3">
                                                                    <label className="form-label">[Codice regione] - Asl di appartenenza</label>
                                                                    <div className="d-flex align-items-center">
                                                                        <select
                                                                            name="idasl"
                                                                            id="idasl"
                                                                            className="form-control"
                                                                            onChange={(e) => {
                                                                                handleChange(e);
                                                                                const selectedIndex = e.target.selectedIndex;
                                                                                const optionElement = e.target.options[selectedIndex];
                                                                                const codRegione = optionElement.dataset.codice || "";
                                                                                setFieldValue("codiceRegione", codRegione);
                                                                                setFieldValue("codiceAsl", optionElement.dataset.asl || "");
                                                                                if (!isPiemonteCodRegione(codRegione)) {
                                                                                    clearPiemonteFields(setFieldValue);
                                                                                }
                                                                                if (!isVenetoCodRegione(codRegione)) {
                                                                                    clearVenetoFields(setFieldValue);
                                                                                }
                                                                            }}
                                                                            onBlur={handleBlur}
                                                                            value={values.idasl || ""}
                                                                            disabled={!values.idComune}
                                                                        >
                                                                            <option value="">Seleziona Asl...</option>
                                                                            {aslOptions.map((asl) => (
                                                                                <option key={asl.id} value={asl.id} data-codice={asl.codRegione} data-asl={asl.codAzienda}>
                                                                                    {`[${asl.codRegione || ""}] ${(asl.regione || "").toUpperCase()} - (${asl.codAzienda || ""}) ${(asl.descrizione || "").toUpperCase()}`}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                    {errors.idasl && touched.idasl && (
                                                                        <div className="text-danger">{errors.idasl}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-3 mb-3">
                                                                    <label className="form-label">Codice Ufficio</label>
                                                                    <input
                                                                        type="text"
                                                                        name="codiceUfficio"
                                                                        id="codiceUfficio"
                                                                        maxLength={10}
                                                                        autoComplete="off"
                                                                        className="form-control"
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                        value={values.codiceUfficio}
                                                                    />
                                                                    {errors.codiceUfficio && touched.codiceUfficio && (
                                                                        <div className="text-danger">{errors.codiceUfficio}</div>
                                                                    )}
                                                                </div>
                                                                {showPiemonteFields && (
                                                                    <>
                                                                        <div className="col-md-4 mb-3">
                                                                            <label className="form-label" htmlFor="codiceAzienda">Codice Azienda (Piemonte)</label>
                                                                            <input
                                                                                type="text"
                                                                                name="codiceAzienda"
                                                                                id="codiceAzienda"
                                                                                maxLength={10}
                                                                                autoComplete="off"
                                                                                className="form-control"
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                value={values.codiceAzienda || ""}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-4 mb-3">
                                                                            <label className="form-label" htmlFor="codiceLuogoAccettazione">Codice Luogo Acc. (Piemonte)</label>
                                                                            <input
                                                                                type="text"
                                                                                name="codiceLuogoAccettazione"
                                                                                id="codiceLuogoAccettazione"
                                                                                maxLength={10}
                                                                                autoComplete="off"
                                                                                className="form-control"
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                value={values.codiceLuogoAccettazione || ""}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {showVenetoFields && (
                                                                    <>
                                                                        <div className="col-md-4 mb-3">
                                                                            <label className="form-label" htmlFor="datiAggiuntivi.nomeUtente">Nome Utente (Veneto)</label>
                                                                            <input
                                                                                type="text"
                                                                                name="datiAggiuntivi.nomeUtente"
                                                                                id="datiAggiuntivi.nomeUtente"
                                                                                maxLength={500}
                                                                                autoComplete="off"
                                                                                className="form-control"
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                value={values.datiAggiuntivi?.nomeUtente || ""}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-4 mb-3">
                                                                            <label className="form-label" htmlFor="datiAggiuntivi.password">Password (Veneto)</label>
                                                                            <input
                                                                                type="text"
                                                                                name="datiAggiuntivi.password"
                                                                                id="datiAggiuntivi.password"
                                                                                maxLength={500}
                                                                                autoComplete="off"
                                                                                className="form-control"
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                value={values.datiAggiuntivi?.password || ""}
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-4 mb-3">
                                                                            <label className="form-label" htmlFor="datiAggiuntivi.codiceFiscale">Codice Fiscale (Veneto)</label>
                                                                            <input
                                                                                type="text"
                                                                                name="datiAggiuntivi.codiceFiscale"
                                                                                id="datiAggiuntivi.codiceFiscale"
                                                                                maxLength={16}
                                                                                autoComplete="off"
                                                                                className="form-control"
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                                value={values.datiAggiuntivi?.codiceFiscale || ""}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label" htmlFor="authRoleFse">
                                                                        Ruolo dell'autore
                                                                    </label>
                                                                    <select
                                                                        id="authRoleFse"
                                                                        name="authRoleFse"
                                                                        className="form-control"
                                                                        value={values.authRoleFse}
                                                                        onChange={handleChange}
                                                                    >
                                                                        <option value="">-- Scegli un ruolo --</option>
                                                                        {authorRoleOptions
                                                                            .slice()
                                                                            .sort((a, b) => a.value.localeCompare(b.value))
                                                                            .map(opt => (
                                                                                <option key={opt.value} value={opt.value}>
                                                                                    {opt.label}
                                                                                </option>
                                                                            ))}
                                                                    </select>
                                                                    {errors.authRoleFse && touched.authRoleFse && (
                                                                        <div className="text-danger mt-1 ml-2">
                                                                            {errors.authRoleFse}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="col-md-4 mb-3">
                                                                    <label className="form-label" htmlFor="tipoCodice">Tipo Codice</label>
                                                                    <select
                                                                        id="tipoCodice"
                                                                        name="tipoCodice"
                                                                        className="form-control"
                                                                        value={values.tipoCodice}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    >
                                                                        <option value={0}>STS.11</option>
                                                                        <option value={1}>HSP.11</option>
                                                                        <option value={2}>RIA.11</option>
                                                                        <option value={3}>Autorizzata</option>
                                                                    </select>
                                                                    {errors.tipoCodice && touched.tipoCodice && (
                                                                        <div className="text-danger mt-1 ml-2">{errors.tipoCodice}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-12 ul-widget3-body">
                                                                    <p className="ul-widget3-text text-small alert alert-info">
                                                                        La configurazione di questi dati è necessaria per predisporre correttamente la struttura all’utilizzo del sistema.
                                                                        <br />
                                                                        Tuttavia, <b>l’abilitazione all’invio verso il Fascicolo Sanitario Elettronico non avviene automaticamente</b>, poiché ogni Regione prevede procedure di onboarding differenti, con requisiti amministrativi e tecnici specifici.
                                                                        <br />
                                                                        <br />
                                                                        <i>
                                                                            <b>
                                                                                Per completare l’attivazione ed essere abilitati all’invio al FSE, è necessario contattare il nostro servizio assistenza all’indirizzo <a href="mailto:info@refertofse.it">info@refertofse.it</a>.
                                                                                <br />
                                                                                Saremo lieti di guidarti passo dopo passo nella procedura prevista per la tua Regione.
                                                                            </b>
                                                                        </i>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <hr className="bg-primary" />
                                                            <h5 className="fw-semibold">3) Selezione Esami</h5>
                                                            <div className="row">
                                                                <div className="col-9 mb-3">
                                                                    <label className="form-label">Ambulatorio</label>
                                                                    <div className="d-flex align-items-center">
                                                                        {(initialValues.idAmbulatorio == 0 || !edit) &&
                                                                            <select
                                                                                name="idAmbulatorio"
                                                                                id="idAmbulatorio"
                                                                                className="form-control"
                                                                                onChange={(e) => handleAmbulatorioChange(e, handleChange)}
                                                                                onBlur={handleBlur}
                                                                                value={values.idAmbulatorio}
                                                                            >
                                                                                <option value="">Seleziona Ambulatorio...</option>
                                                                                {ambulatoriOptions.map((amb) => (
                                                                                    <option key={amb.id} value={amb.id}>
                                                                                        {amb.descrizione.toUpperCase()}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        }
                                                                        {(initialValues.idAmbulatorio > 0 && edit) &&
                                                                            <>
                                                                                <input
                                                                                    type="hidden"
                                                                                    name="idAmbulatorio"
                                                                                    id="idAmbulatorio"
                                                                                    value={values.idAmbulatorio}
                                                                                />
                                                                                <input
                                                                                    type="text"
                                                                                    name="ambulatorioText"
                                                                                    className="form-control"
                                                                                    disabled={true}
                                                                                    value={values.ambulatorioAsString}
                                                                                />
                                                                            </>
                                                                        }
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-primary mx-4"
                                                                            disabled={isEsamiButtonDisabled}
                                                                            onClick={() => setEsamiModalOpen(true)}
                                                                        >
                                                                            {numEsami}
                                                                        </button>
                                                                    </div>
                                                                    {errors.idAmbulatorio && touched.idAmbulatorio && (
                                                                        <div className="text-danger">{errors.idAmbulatorio}</div>
                                                                    )}
                                                                </div>
                                                                <div className="col-12 ul-widget3-body">
                                                                    <p className="ul-widget3-text text-small alert alert-success">
                                                                        Quando selezioni l’ambulatorio, il sistema associa automaticamente tutti gli esami del Nomenclatore Nazionale collegati a quell’ambulatorio.
                                                                        <br />
                                                                        Per vedere quali esami verrranno inseriti, basta <b>cliccare sul pulsante</b> accanto al menu di selezione.
                                                                        <br />
                                                                        <br />
                                                                        <i><b>Una piccola nota: dopo il salvataggio, per eventuali modifiche a questa sezione sarà necessario l’intervento del nostro servizio assistenza. <br /> Se pensi che possa servirti una variazione in futuro, scrivici pure: siamo qui per aiutarti.</b></i>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <hr className="bg-primary mt-2" />
                                                            <h5 className="fw-semibold">4) Configurazione Referto</h5>
                                                            {/* 4) Configurazione Referto */}
                                                            <div className="row g-3 mb-3">
                                                                {/* PREVIEW INTESTAZIONE */}
                                                                <div className="col-12 col-lg-8">
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <label className="form-label mb-0">Intestazione referto</label>
                                                                        <small className="text-muted">Anteprima</small>
                                                                    </div>

                                                                    <div
                                                                        className="border rounded p-3"
                                                                        name="intestazioneReferto"
                                                                        id="intestazioneReferto"
                                                                        style={{ minHeight: "120px" }}
                                                                        dangerouslySetInnerHTML={{ __html: values.intestazioneReferto }}
                                                                    />

                                                                    <div className="d-flex justify-content-end mt-2">
                                                                        <Button
                                                                            variant="info"
                                                                            className="w-100 w-md-auto"
                                                                            onClick={() => {
                                                                                setContentReferto(values.intestazioneReferto);
                                                                                setRefertoModalOpen(true);
                                                                            }}
                                                                        >
                                                                            <i className="i-File-Clipboard-Text--Image"></i>&nbsp;&nbsp;Modifica Intestazione Referto
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                                {/* COLONNA DESTRA: IMPOSTAZIONI + FIRMA */}
                                                                <div className="col-12 col-lg-4">
                                                                    <div className="border rounded p-3">

                                                                        {/* IMPOSTAZIONI */}
                                                                        <div className="mb-3">
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <label className="form-label mb-0">Impostazioni</label>
                                                                            </div>

                                                                            <div className="row g-2">
                                                                                <div className="col-12">
                                                                                    <label className="form-label">Dimensione Titolo Referto</label>
                                                                                    <select
                                                                                        name="dimensioneTitolo"
                                                                                        id="dimensioneTitolo"
                                                                                        className="form-control"
                                                                                        value={values.dimensioneTitolo}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                    >
                                                                                        <option key={0} value={""}>Seleziona...</option>
                                                                                        {Array.from({ length: 21 }, (_, i) => i + 8).map(px => (
                                                                                            <option key={px} value={px}>{px}px</option>
                                                                                        ))}
                                                                                    </select>
                                                                                </div>
                                                                                <div className="col-12">
                                                                                    <div className="form-check form-switch">
                                                                                        <input
                                                                                            className="form-check-input"
                                                                                            type="checkbox"
                                                                                            id="addSezioniFse"
                                                                                            name="addSezioniFse"
                                                                                            checked={!!values.addSezioniFse}
                                                                                            onChange={(e) => setFieldValue("addSezioniFse", e.target.checked)}
                                                                                        />
                                                                                        <label className="form-check-label" htmlFor="addSezioniFse">
                                                                                            Aggiungi sezioni FSE facoltative nel referto
                                                                                        </label>
                                                                                    </div>
                                                                                    <div className="form-check form-switch mb-2">
                                                                                        <input
                                                                                            className="form-check-input"
                                                                                            type="checkbox"
                                                                                            id="addFirma"
                                                                                            name="addFirma"
                                                                                            checked={!!values.addFirma}
                                                                                            onChange={(e) => setFieldValue("addFirma", e.target.checked)}
                                                                                        />
                                                                                        <label className="form-check-label" htmlFor="addFirma">
                                                                                            Aggiungi firma in automatico
                                                                                        </label>
                                                                                    </div>
                                                                                </div>

                                                                                <div className="col-12">
                                                                                    <label className="form-label">Allineamento Firma Medico</label>
                                                                                    <select
                                                                                        name="allineamentoFirma"
                                                                                        id="allineamentoFirma"
                                                                                        className="form-control"
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        value={values.allineamentoFirma}
                                                                                    >
                                                                                        <option value="0">Sinistra</option>
                                                                                        <option value="1">Centro</option>
                                                                                        <option value="2">Destra</option>
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* PREVIEW FIRMA */}
                                                                        <div>
                                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                <label className="form-label mb-0">Firma Medico</label>
                                                                                <small className="text-muted">Anteprima</small>
                                                                            </div>

                                                                            <div
                                                                                className="border rounded p-3"
                                                                                name="firmaMedico"
                                                                                id="firmaMedico"
                                                                                style={{ minHeight: "120px" }}
                                                                                dangerouslySetInnerHTML={{ __html: values.firmaMedico }}
                                                                            />

                                                                            <div className="d-flex justify-content-end mt-2">
                                                                                <Button
                                                                                    variant="info"
                                                                                    className="w-100"
                                                                                    onClick={() => setFirmaModalOpen(true)}
                                                                                >
                                                                                    <i className="i-Pen-2"></i>&nbsp;&nbsp;Modifica Firma Medico
                                                                                </Button>
                                                                            </div>
                                                                        </div>

                                                                    </div>
                                                                </div>

                                                            </div>

                                                            <hr className="bg-primary mt-4" />
                                                            <div className="d-flex justify-content-end">
                                                                <Button
                                                                    className="w-25"
                                                                    variant="primary"
                                                                    type="submit"
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <i className="i-Disk"></i>&nbsp;&nbsp;{isSubmitting ? "Salvataggio..." : "Salva"}
                                                                </Button>
                                                            </div>

                                                            {/* MODALE ESAMI */}
                                                            <Modal
                                                                id="modaleEsami"
                                                                show={isEsamiModalOpen}
                                                                size="lg"
                                                                centered
                                                                onHide={() => setEsamiModalOpen(false)}
                                                                scrollable
                                                                backdrop="static"
                                                            >
                                                                <Modal.Header className="bg-primary text-white d-flex flex-wrap justify-content-between align-items-center">
                                                                    <h5 className="text-white">Lista Esami</h5>
                                                                    <i
                                                                        className="i-Close-Window cursor-pointer text-18"
                                                                        onClick={() => setEsamiModalOpen(false)}
                                                                    ></i>
                                                                </Modal.Header>
                                                                <Modal.Body>
                                                                    <div>
                                                                        <ol>
                                                                            {esami && esami.map((esame, index) => (
                                                                                <li className="fw-bold" key={index}>
                                                                                    <span className="fw-light">{esame.descrizione?.toUpperCase() ?? esame.esameAsString.toUpperCase()}</span>
                                                                                </li>
                                                                            ))}
                                                                        </ol>
                                                                    </div>
                                                                </Modal.Body>
                                                                <Modal.Footer>
                                                                    <button
                                                                        className="btn btn-primary"
                                                                        type="button"
                                                                        onClick={() => setEsamiModalOpen(false)}
                                                                    >
                                                                        Chiudi
                                                                    </button>
                                                                </Modal.Footer>
                                                            </Modal>

                                                            {/* MODALE REFERTO (riutilizzabile) */}
                                                            <EditorHtmlModal
                                                                id="modaleRerferto"
                                                                show={isRefertoModalOpen}
                                                                title="Modifica Intestazione Referto"
                                                                content={contentReferto}
                                                                onChange={setContentReferto}
                                                                onApply={() => {
                                                                    setFieldValue("intestazioneReferto", contentReferto);
                                                                    setRefertoModalOpen(false);
                                                                }}
                                                                onCancel={() => {
                                                                    setContentReferto(values.intestazioneReferto);
                                                                    setRefertoModalOpen(false);
                                                                }}
                                                                variables={variabiliHtml}
                                                                variablesLoading={isVariabiliHtmlLoading}
                                                                variablesError={variabiliHtmlError}
                                                                variablesCollapsible={true}
                                                                size="xl"
                                                                adattaStyle={false}
                                                                nascondiSalva={true}
                                                            />

                                                            {/* MODALE FIRMA (riutilizzabile) */}
                                                            <EditorHtmlModal
                                                                id="modaleFirma"
                                                                show={isFirmaModalOpen}
                                                                title="Modifica Firma Medico"
                                                                content={contentFirma}
                                                                onChange={(content) => {
                                                                    setFieldValue("firmaMedico", content);
                                                                }}
                                                                onClose={() => {
                                                                    setFirmaModalOpen(false);
                                                                    setContentFirma(values.firmaMedico);
                                                                }}
                                                                size="xl"
                                                                adattaStyle={false}
                                                                nascondiSalva={true}
                                                            />

                                                        </Form>
                                                    }
                                                </>
                                            );
                                        }}
                                    </Formik>
                                </Card.Body>
                            </Card>
                            <NotificationContainer />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

const validationSchema = Yup.object().shape({
    // Tutti i campi obbligatori:
    intestazione: Yup.string().required("Campo obbligatorio"),
    idComune: Yup.string().required("Campo obbligatorio"),
    cap: Yup.string()
        .required("Campo obbligatorio")
        .matches(/^[0-9]*$/, "Solo numeri sono ammessi")
        .max(5, "Il CAP non puo superare i 5 caratteri"),
    indirizzo: Yup.string().required("Campo obbligatorio"),
    telefono: Yup.string().required("Campo obbligatorio"),
    partitaIva: Yup.string().max(11, "Max 11 caratteri"),
    codiceFiscale: Yup.string().required("Campo obbligatorio").max(16, "Max 16 caratteri"),
    idAmbulatorio: Yup.string().required("Campo obbligatorio"),
});

export default InfoAccount;
